/**
 * Helpers para formateo de fechas usando date-fns
 * IMPORTANTE: Todas las fechas se muestran según la zona horaria del usuario
 * pero se guardan en UTC en la base de datos
 */

import {
  format,
  formatDistanceToNow,
  parseISO,
} from "date-fns";
import { toZonedTime as tzToZonedTime, fromZonedTime as tzFromZonedTime } from "date-fns-tz";
import { es, enUS } from "date-fns/locale";

export {
  format,
  formatDistanceToNow,
  parseISO,
} from "date-fns";

export { es, enUS };

/**
 * Obtiene la zona horaria del usuario desde el navegador
 */
export function getUserTimezone(): string {
  if (typeof window !== "undefined") {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return "UTC";
}

/**
 * Convierte una fecha UTC a la zona horaria del usuario
 */
export function utcToUserTimezone(date: Date | string | number, userTimezone?: string): Date {
  const dateObj = typeof date === "string" ? parseISO(date) : new Date(date);
  const tz = userTimezone ?? getUserTimezone();
  return tzToZonedTime(dateObj, tz);
}

/**
 * Convierte una fecha de la zona horaria del usuario a UTC
 * USAR ESTO antes de guardar en base de datos
 */
export function userTimezoneToUTC(date: Date, userTimezone?: string): Date {
  const tz = userTimezone ?? getUserTimezone();
  return tzFromZonedTime(date, tz);
}

/**
 * Formatea una fecha a formato corto (DD/MM/YYYY)
 * La fecha se convierte automáticamente a la zona horaria del usuario
 */
export function formatShort(date: Date | string | number, userTimezone?: string): string {
  const dateObj = typeof date === "string" ? parseISO(date) : new Date(date);
  const userDate = utcToUserTimezone(dateObj, userTimezone);
  return format(userDate, "dd/MM/yyyy");
}

/**
 * Formatea una fecha a formato largo (DD de MMMM de YYYY)
 * La fecha se convierte automáticamente a la zona horaria del usuario
 */
export function formatLong(
  date: Date | string | number,
  locale = es,
  userTimezone?: string
): string {
  const dateObj = typeof date === "string" ? parseISO(date) : new Date(date);
  const userDate = utcToUserTimezone(dateObj, userTimezone);
  return format(userDate, "dd 'de' MMMM 'de' yyyy", { locale });
}

/**
 * Formatea una fecha a formato relativo (hace 2 días)
 * La fecha se convierte automáticamente a la zona horaria del usuario
 */
export function formatRelativeTime(
  date: Date | string | number,
  locale = es,
  userTimezone?: string
): string {
  const dateObj = typeof date === "string" ? parseISO(date) : new Date(date);
  const userDate = utcToUserTimezone(dateObj, userTimezone);
  return formatDistanceToNow(userDate, { addSuffix: true, locale });
}

/**
 * Formatea una fecha con hora (DD/MM/YYYY HH:mm)
 * La fecha se convierte automáticamente a la zona horaria del usuario
 */
export function formatDateTime(date: Date | string | number, userTimezone?: string): string {
  const dateObj = typeof date === "string" ? parseISO(date) : new Date(date);
  const userDate = utcToUserTimezone(dateObj, userTimezone);
  return format(userDate, "dd/MM/yyyy HH:mm");
}

/**
 * Formatea solo la hora (HH:mm)
 * La fecha se convierte automáticamente a la zona horaria del usuario
 */
export function formatTime(date: Date | string | number, userTimezone?: string): string {
  const dateObj = typeof date === "string" ? parseISO(date) : new Date(date);
  const userDate = utcToUserTimezone(dateObj, userTimezone);
  return format(userDate, "HH:mm");
}
